import { ClipboardIcon, CurrencyDollarIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { CardContent } from "@/shared/components/ui/card";
import { CardDescription } from "@/shared/components/ui/card";
import { CardFooter } from "@/shared/components/ui/card";
import { CardHeader } from "@/shared/components/ui/card";
import { CardTitle } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { useCanary } from "@/shared/hooks/useCanary";

import {
  useGetLatestPayoutTermQuery,
  useGetReferrerAgreementStatusQuery,
  useGetReferrerCodesQuery,
} from "./referrals/-api/referrals";
import ReferralEarningsChartContainer from "./referrals/-components/ReferralEarningsChartContainer";
import ReferralInviteModal from "./referrals/-components/ReferralInviteModal";
import ReferralMetrics from "./referrals/-components/ReferralMetrics";
import ReferralTermsOfService from "./referrals/-components/ReferralTermsOfService";

const ReferralDashboardSkeleton = () => (
  <div className="flex flex-col gap-4 md:gap-9">
    <Skeleton className="h-14 rounded md:h-[104px] md:rounded-2xl" />
    <Skeleton className="h-[578px] rounded-2xl" />
    <Skeleton className="h-[470px] rounded-2xl" />
  </div>
);

function ReferralDashboard() {
  const hasReferralFeature = useCanary("referralProgram");
  const accountId = useSelector(selectMaybeActiveAccountEid);
  const queryAccountId = hasReferralFeature ? accountId : skipToken;
  const { data: referrerAcceptedTermsOfService, isLoading: referrerAgreementLoading } =
    useGetReferrerAgreementStatusQuery(queryAccountId);
  const { data: payoutTermData, isLoading: latestPayoutTermLoading } = useGetLatestPayoutTermQuery(queryAccountId);

  const positionQuery = useGetAccountPositionQuery(hasReferralFeature ? "817464952755" : skipToken); // todo

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const { data: referrerCodes, isLoading: referrerCodesLoading } = useGetReferrerCodesQuery(queryAccountId);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log("Copied:", text);
    });
  };

  if (!hasReferralFeature) return null;
  if (referrerAgreementLoading || referrerCodesLoading || latestPayoutTermLoading) {
    return <ReferralDashboardSkeleton />;
  }

  if (!referrerAcceptedTermsOfService) {
    return <ReferralTermsOfService />;
  }

  const referralCodeInfo = referrerCodes?.referrerCodes[0]; // TODO: when users are allowed to have multiple referral codes, this should return all referrer codes
  const purchasePayoutTerms =
    payoutTermData?.latestPayoutTerm.filter((term) => term.payoutTypeName === "purchase") || [];
  const leasePayoutTerms = payoutTermData?.latestPayoutTerm.filter((term) => term.payoutTypeName === "lease") || [];

  if (!referralCodeInfo) {
    return (
      <Card className="p-4 md:p-8">
        <div className="animate-in rounded border border-red-950/20 bg-red-50 px-4 py-2 fade-in">
          <p className="text-sm font-medium text-red-950">{t`Error Retrieving Referral Code`}</p>
          <p className="text-sm text-red-950">{t`Failed to retrieve your referral code. Please try again later.`}</p>
        </div>
      </Card>
    );
  }

  const selectedReferralCode = referralCodeInfo?.referrer_code || "Error";
  const referralLink = `https://monetarymetals.com/ref/${selectedReferralCode}`;

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<ReferralDashboardSkeleton />}
      shouldRetry
      translatedErrorMessage={t`There was an issue loading your referral information. Please try again later or contact your RM if the problem persists.`}
      queries={[positionQuery]} // todo
    >
      <div className="flex flex-col gap-4 md:gap-9">
        <Card className="p-4 md:p-8">
          <CardTitle className="pb-4 text-3xl font-bold md:text-2xl">{t`Referral Program`}</CardTitle>
          <ReferralMetrics />
        </Card>

        <ReferralEarningsChartContainer />

        <Card>
          <CardHeader>
            <CardTitle>{t`Your Referral Information`}</CardTitle>
            <CardDescription>{t`Share your code or link with friends`}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div>
              <label htmlFor="referral-code" className="text-sm font-medium">
                {t`Referral Code`}{" "}
                {/* TODO: when users are allowed to have multiple referrer codes, this should become a dropdown that dynamic chnages the referral link as well. */}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <code className="rounded bg-muted/90 px-2 py-1">{selectedReferralCode}</code>
                <Button variant="outline" size="sm" onClick={() => handleCopyToClipboard(selectedReferralCode)}>
                  <ClipboardIcon className="mr-1 h-4 w-4" />
                  {t`Copy`}
                </Button>
              </div>
            </div>

            <div>
              <label htmlFor="referral-link" className="text-sm font-medium">
                {t`Referral Link`}
              </label>
              <div className="mt-1 flex items-center gap-2">
                <Input className="rounded bg-muted/90" value={referralLink} readOnly />
                <Button variant="outline" size="sm" onClick={() => handleCopyToClipboard(referralLink)}>
                  <ClipboardIcon className="mr-1 h-4 w-4" />
                  {t`Copy`}
                </Button>
              </div>
            </div>
          </CardContent>

          <CardFooter>
            <Button size="lg" onClick={() => setIsInviteModalOpen(true)}>
              <PaperAirplaneIcon className="mr-2 h-5 w-5" />
              {t`Send Invite`}
            </Button>
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t`Referral Rewards`}</CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              {t`Here's how your referral rewards are calculated and paid out.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CardTitle className="text-sm">{t`Metal Purchases`}</CardTitle>
            {purchasePayoutTerms.map((term) => (
              <div key={`purchase-${term.contractPayoutTermsId}`} className="flex items-start gap-3">
                <CurrencyDollarIcon className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                <div>
                  <p className="text-primary">
                    {t`${term.percentage}% on purchases made by referred users (${term.percentage * 100} oz on 100 oz purchased) from ${term.lowerBound} oz to ${term.upperBound || "∞"} oz`}
                  </p>
                </div>
              </div>
            ))}
            <p className="text-sm text-muted-foreground">
              {t`Paid quarterly on last year's deposits. Gold must remain in LYSA for 1 year to get reward.`}
            </p>
            <div className="mt-4">
              <CardTitle className="text-sm">{t`Leases`}</CardTitle>
              {leasePayoutTerms.map((term) => (
                <div key={`lease-${term.contractPayoutTermsId}`} className="flex items-start gap-3">
                  <CurrencyDollarIcon className="mt-1 h-5 w-5 flex-shrink-0 text-primary" />
                  <div>
                    <p className="text-primary">
                      {t`${term.percentage}% on leased metal (${term.percentage * 100} oz on 100 oz deployed into leases)`}
                    </p>
                  </div>
                </div>
              ))}
              <p className="text-sm text-muted-foreground">{t`Paid on lease inception`}</p>
            </div>
          </CardContent>
        </Card>

        <ReferralInviteModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          referralCodeInfo={referralCodeInfo}
        />
      </div>
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/referrals")({
  component: ReferralDashboard,
  context: () => ({ permissions: "referralProgram" }),
});
